import {
  isFormField,
  LIQUID_VIRTUAL_FIELDS,
  parseFieldMarker,
} from '@tronche2tek/shared-types';

import type {
  CardSides,
  CardTemplateDto,
  OutputFormat,
  PreflightInput,
} from '../../../api/cards';
import { fieldsForSides, sourceForType } from '../../../api/cards';
import type { CardsSearch, IseeQueryState } from '../cards-url-state';
import { pickIseeQuery } from '../cards-url-state';
import { toSessionQuery } from '../isee-filters';

// ─────────────────────────────────────────────────────────────────────────────
// CSV
// ─────────────────────────────────────────────────────────────────────────────

const CSV_DELIMITER_CANDIDATES = [',', ';', '\t', '|'] as const;
export type CsvDelimiter = (typeof CSV_DELIMITER_CANDIDATES)[number];

export const CSV_DELIMITER_OPTIONS: {
  value: CsvDelimiter;
  label: string;
  key: string;
}[] = [
  { value: ',', label: ',', key: 'comma' },
  { value: ';', label: ';', key: 'semicolon' },
  { value: '\t', label: '↹', key: 'tab' },
  { value: '|', label: '|', key: 'pipe' },
];

/** The candidate that splits the first non-empty line into the most cells. */
export function detectCsvDelimiter(text: string): CsvDelimiter {
  const firstLine = text.split(/\r?\n/).find((l) => l.trim().length > 0) ?? '';
  let best: CsvDelimiter = ',';
  let bestCount = 0;
  for (const c of CSV_DELIMITER_CANDIDATES) {
    const count = firstLine.split(c).length - 1;
    if (count > bestCount) {
      bestCount = count;
      best = c;
    }
  }
  return best;
}

/** A cell with its quoted runs removed — what is left is what a split sees. */
function outsideQuotes(cell: string): string {
  return cell.replace(/"[^"]*"|'[^']*'/g, '');
}

/**
 * Whether a one-column parse is evidence of the wrong separator rather than of
 * a one-column file.
 *
 * The tell is the header still holding one of the other candidates *unquoted*:
 * `annee;ville` split on ',' is one column that plainly wanted to be two,
 * while `annee` is simply a file with one column — which a template needing a
 * single variable legitimately has, and which used to be reported as a parsing
 * error with its preview hidden behind it. Quoting decides the difference for
 * `"annee;ville"`, where the separator is part of the column's name and the
 * backend reads one column too; hence the header as the file spells it rather
 * than the unquoted one shown in the table.
 */
export function looksMisparsed(
  rawColumns: string[],
  delimiter: CsvDelimiter,
): boolean {
  if (rawColumns.length > 1) {
    return false;
  }
  const header = outsideQuotes(rawColumns[0] ?? '');
  return CSV_DELIMITER_CANDIDATES.some(
    (candidate) => candidate !== delimiter && header.includes(candidate),
  );
}

export interface CsvPreview {
  columns: string[];
  /** The header as the file spells it, quotes kept — see {@link looksMisparsed}. */
  rawColumns: string[];
  /** The first five data rows, for the preview table. */
  rows: string[][];
  total: number;
}

const EMPTY_CSV_PREVIEW: CsvPreview = {
  columns: [],
  rawColumns: [],
  rows: [],
  total: 0,
};

const unquote = (c: string) => c.trim().replace(/^["']|["']$/g, '');

/**
 * Header, a handful of rows and a count, for the preview table only — the
 * backend parses the file itself, so this never has to be exhaustive. The
 * header comes back twice: unquoted for the table, and as the file spells it
 * for {@link looksMisparsed}, which quoting alone can settle.
 */
export function parseCsvPreview(
  raw: string | null,
  delimiter: CsvDelimiter,
): CsvPreview {
  if (!raw) {
    return EMPTY_CSV_PREVIEW;
  }
  const lines = raw.split(/\r?\n/).filter((l) => l.trim().length > 0);
  const rawColumns = (lines[0] ?? '').split(delimiter).map((c) => c.trim());
  const columns = rawColumns.map(unquote);
  return {
    columns,
    rawColumns,
    rows: lines.slice(1, 6).map((line) => line.split(delimiter).map(unquote)),
    total: Math.max(lines.length - 1, 0),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Where each template variable's value will come from
// ─────────────────────────────────────────────────────────────────────────────

export interface FieldStatus {
  name: string;
  source: 'csv' | 'virtual' | 'default' | 'missing';
}

/** 'missing' is the one that blocks generation: nothing would fill it. */
export function computeFieldStatus(
  templateVars: string[],
  csvColumns: string[],
  defaults: Record<string, string>,
): FieldStatus[] {
  const csvSet = new Set(csvColumns);
  return templateVars.map((name) => ({
    name,
    source: csvSet.has(name)
      ? 'csv'
      : LIQUID_VIRTUAL_FIELDS.has(name)
        ? 'virtual'
        : name in defaults
          ? 'default'
          : 'missing',
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// The form
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Everything the operator picks before a batch runs. The template SVG and the
 * CSV are held as Files (they never reach the URL); the rest mirrors the
 * URL-persisted search state so a shared link refills the form.
 */
export interface CardGenFields {
  templateId: string;
  templateFile: File | null;
  csvFile: File | null;
  delimiter: CsvDelimiter;
  /** False once the operator overrides the auto-detected delimiter. */
  delimiterIsAuto: boolean;
  outputFormat: OutputFormat;
  sides: CardSides;
  /** Field the cards come out ordered by; '' leaves the order to the pipeline. */
  sortBy: string;
  /** Values for the template's `form:` fields, keyed by variable name. */
  values: Record<string, string>;
  logins: string[];
  iseeQuery: IseeQueryState;
}

/**
 * The form's starting values, pre-filled from the URL-persisted search state.
 * Typed explicitly: `useForm` derives its field paths from what this returns,
 * so inferring `null` for the two File fields would drop them from the paths.
 */
export function initialCardGenFields(search?: CardsSearch): CardGenFields {
  return {
    iseeQuery: pickIseeQuery(search),
    logins: search?.logins ?? [],
    csvFile: null,
    templateFile: null,
    templateId: '',
    delimiter: ',',
    delimiterIsAuto: true,
    outputFormat: search?.format ?? 'pdf',
    sides: search?.sides ?? 'front',
    sortBy: search?.sortBy ?? '',
    values: {},
  };
}

export interface BatchRequest {
  /** Which recipient list the selected template's type implies. */
  source: 'csv' | 'isee' | 'lucca';
  templateId: string;
  /** Always present — a saved template is staged as a File before this runs. */
  templateFile: File;
  csvFile: File | null;
  delimiter: CsvDelimiter;
  outputFormat: OutputFormat;
  sides: CardSides;
  /** Field to order the output by; '' leaves the order to the pipeline. */
  sortBy: string;
  iseeQuery: IseeQueryState;
  logins: string[];
  /** The `form:` fields actually needed, and the values entered for them. */
  formFields: string[];
  values: Record<string, string>;
}

/**
 * The pre-flight request for a batch. Lucca posts JSON (a login list); ISEE
 * posts its filter query, and everything else the CSV plus the template.
 */
export function buildBatchPreflightInput(req: BatchRequest): PreflightInput {
  const values = req.formFields.length ? req.values : undefined;
  // '' is the form's "leave the order alone"; the field is simply not sent.
  const sortBy = req.sortBy || undefined;

  if (req.source === 'lucca') {
    return {
      source: 'lucca',
      templateId: req.templateId,
      logins: req.logins,
      outputFormat: req.outputFormat,
      sides: req.sides,
      sortBy,
      values,
    };
  }
  if (req.source === 'isee') {
    // The cohort comes from the live tree selection — the preview is
    // informative only, not a prerequisite.
    const session = toSessionQuery(req.iseeQuery);
    // `canSubmit` requires a cohort before the submit path can reach this, but
    // the type cannot see that — better a loud error than a partial node posted.
    if (!session) {
      throw new Error('ISEE batch without a cohort');
    }
    return {
      source: 'isee',
      session,
      templateId: req.templateId || undefined,
      template: req.templateId ? undefined : req.templateFile,
      outputFormat: req.outputFormat,
      sides: req.sides,
      sortBy,
      values,
    };
  }
  // Same shape as the year above: guaranteed by `canSubmit`, opaque to types.
  if (!req.csvFile) {
    throw new Error('CSV batch without a CSV file');
  }
  return {
    source: 'csv',
    csv: req.csvFile,
    template: req.templateFile,
    templateId: req.templateId || undefined,
    delimiter: req.delimiter,
    outputFormat: req.outputFormat,
    sides: req.sides,
    sortBy,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// What the chosen fields add up to
// ─────────────────────────────────────────────────────────────────────────────

/** What a chosen template's SVGs turned out to carry (see useCardTemplatePick). */
export interface TemplateMeta {
  frontVars: string[];
  backVars: string[];
  defaults: Record<string, string>;
}

export interface DerivedBatch {
  selectedTpl?: CardTemplateDto;
  /** Which recipient list the template's type implies. */
  source: 'csv' | 'isee' | 'lucca';
  /** A single-side choice only applies to a template that has a verso. */
  effectiveSides: CardSides;
  /** The variables the generated side(s) actually use. */
  effectiveVars: string[];
  allTemplateVars: string[];
  /** Those flagged `form:` — one value each, for the whole batch. */
  formFields: string[];
  /** The columns a CSV for this template should carry. */
  csvTemplateCols: string[];
  /** The fields the generated output can be ordered by; empty with no template. */
  sortFields: string[];
  fieldStatuses: FieldStatus[] | null;
  hasMissing: boolean;
  hasEmptyFormField: boolean;
  hasRecipients: boolean;
  hasTemplate: boolean;
  /** Everything the pre-flight needs is chosen, and nothing is wrong. */
  canSubmit: boolean;
}

/** The saved template behind the chosen id, when there is one. */
export function selectedTplFor(
  templateId: string,
  savedTemplates: CardTemplateDto[],
): CardTemplateDto | undefined {
  return savedTemplates.find((tpl) => tpl.id === templateId);
}

/** A single-side choice only applies to a template that has a verso — a stale
 *  shared URL (`?sides=back` with a back-less template) falls back. */
export function effectiveSidesFor(
  selectedTpl: CardTemplateDto | undefined,
  sides: CardSides,
): CardSides {
  return selectedTpl?.backSide ? sides : 'both';
}

/**
 * The columns a CSV for this template should carry. A variable the template
 * binds to a source is resolved per recipient at generation; handing the
 * operator a column for it would only let a stray value override the binding.
 */
export function csvTemplateColsFor(
  effectiveVars: string[],
  defaults: Record<string, string>,
): string[] {
  return [
    'upn',
    ...effectiveVars.filter(
      (v) => !LIQUID_VIRTUAL_FIELDS.has(v) && !parseFieldMarker(defaults[v]),
    ),
  ];
}

/**
 * The fields the output can be ordered by: the chosen template's own
 * variables, for the sides being generated, and nothing else.
 *
 * `upn` and `displayName` are *not* added on top. Every recipient does carry
 * both — the resolve step looks each one up in Entra whatever the source — but
 * a template (an `other`-typed one especially) can render neither, and a
 * picker offering a field its template has nothing to do with is a picker
 * nobody can reason about. `upn` is still offered when the template renders
 * `{{ upn }}`, on the same footing as any other variable it uses.
 *
 * Empty without a template, and empty for a template that renders no variable
 * at all. That emptiness is the single fact the picker's disabled state and
 * `effectiveSortBy`'s stale-choice clearing both follow from.
 *
 * `photo` is dropped: it is image bytes, not something to sort on. Whatever
 * fills a variable — a CSV column, an `entra:`/`isee:`/`lucca:` binding — it
 * is resolved per card and so can be ordered by; one that nothing fills is
 * refused at pre-flight before ordering gets a say.
 */
export function sortFieldsFor(
  effectiveVars: string[],
  hasTemplate: boolean,
): string[] {
  if (!hasTemplate) {
    return [];
  }
  return [...new Set(effectiveVars.filter((v) => v !== 'photo'))];
}

/**
 * The sort choice, but only while the chosen template still offers it.
 *
 * The field survives in the form (and in a shared URL) longer than the
 * template that justified it: swapping template, or switching to one side
 * whose SVG uses fewer variables, can leave a choice nothing will resolve.
 * Posting it would be a 400 at pre-flight, so it degrades to no ordering.
 */
export function effectiveSortBy(sortBy: string, sortFields: string[]): string {
  if (sortFields.includes(sortBy)) {
    return sortBy;
  }
  return '';
}

/** Whether the chosen source has anyone to generate for. */
export function hasRecipientsFor(
  values: Pick<CardGenFields, 'csvFile' | 'iseeQuery' | 'logins'>,
  source: 'csv' | 'isee' | 'lucca',
): boolean {
  if (source === 'csv') {
    return !!values.csvFile;
  }
  if (source === 'isee') {
    return toSessionQuery(values.iseeQuery) !== null;
  }
  return values.logins.length > 0;
}

/**
 * Everything the form's values add up to, in one pure pass: which source
 * applies, which variables the batch needs, what the CSV supplies and what is
 * still missing.
 *
 * Pure on purpose. The submit path calls it with `form.state.values` at the
 * moment of submission, so the container does not have to subscribe to nine
 * fields just to build a request; the form components memoise it for rendering.
 */
/** The form fields deriveBatch actually reads — the delimiter pair, the output
 *  format and the chosen sort field have no bearing on what a batch adds up
 *  to (the sort *candidates* are derived, the choice among them is not). */
export type DeriveInputs = Omit<
  CardGenFields,
  'delimiter' | 'delimiterIsAuto' | 'outputFormat' | 'sortBy'
>;

export function deriveBatch(
  values: DeriveInputs,
  template: TemplateMeta,
  savedTemplates: CardTemplateDto[],
  /** Null until a CSV is loaded. Parsed by the caller so splitting a large
   *  file is not redone every time an unrelated field changes. */
  csv: CsvPreview | null,
): DerivedBatch {
  const selectedTpl = selectedTplFor(values.templateId, savedTemplates);
  const source = sourceForType(selectedTpl?.type);
  const effectiveSides = effectiveSidesFor(selectedTpl, values.sides);
  const effectiveVars = fieldsForSides(
    effectiveSides,
    template.frontVars,
    template.backVars,
  );

  const needed = new Set(effectiveVars);
  const formFields = Object.entries(template.defaults)
    .filter(([k, v]) => isFormField(v) && needed.has(k))
    .map(([k]) => k)
    .sort();

  const csvColumns = csv?.columns ?? [];
  const noStatuses =
    !values.csvFile ||
    !values.templateFile ||
    (csvColumns.length === 0 && effectiveVars.length === 0);
  const fieldStatuses = noStatuses
    ? null
    : computeFieldStatus(effectiveVars, csvColumns, template.defaults);
  const hasMissing =
    fieldStatuses?.some((f) => f.source === 'missing') ?? false;

  // A blank `form:` field would render as an empty value on the card.
  const hasEmptyFormField = formFields.some(
    (f) => (values.values[f] ?? '').trim() === '',
  );

  const hasRecipients = hasRecipientsFor(values, source);
  const csvTemplateCols = csvTemplateColsFor(effectiveVars, template.defaults);
  const hasTemplate = !!values.templateId || !!values.templateFile;

  return {
    selectedTpl,
    source,
    effectiveSides,
    effectiveVars,
    allTemplateVars: [
      ...new Set([...template.frontVars, ...template.backVars]),
    ],
    formFields,
    csvTemplateCols,
    sortFields: sortFieldsFor(effectiveVars, hasTemplate),
    fieldStatuses,
    hasMissing,
    hasEmptyFormField,
    hasRecipients,
    hasTemplate,
    canSubmit:
      hasRecipients &&
      !!values.templateFile &&
      !hasMissing &&
      !hasEmptyFormField,
  };
}
